import { INITIAL_PAGE, PAGINATION_LIMIT } from '@repo/toolkit/constants/pagination'
import { useNavigator } from '@repo/toolkit/use-navigator'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useUrl } from './use-url'

const PAGINATION_PARAMS = ['page', 'limit', 'sortBy', 'q']
// Written when the applied filters are empty, so the URL keeps that choice instead of falling back to the defaults
const NO_FILTERS_PARAM = 'filters=none'

export type UseFiltersResponse<TFilters> = {
  filter: TFilters | undefined
  selectedFilter: TFilters | undefined
  onFilterChange: (newFilter?: TFilters) => void
  onSelectedFilterChange: (newFilter?: TFilters) => void
  resetFilters: () => void
}

export const useFilters = <TFilters>(data: {
  initialFilters: TFilters
  transformFilterToUrl?: (filters?: TFilters) => string
  transformUrlToFilter?: (searchParams: URLSearchParams) => TFilters
  options?: {
    allowSearchParam?: boolean
  }
}): UseFiltersResponse<TFilters> => {
  const { transformFilterToUrl, transformUrlToFilter, initialFilters, options } = data
  const [searchParams, setSearchParams] = useSearchParams()
  const { readPagination } = useUrl()
  const { navigate } = useNavigator()
  const page = searchParams.get('page')

  const transformFromUrl = (): TFilters | undefined => {
    if (page === null || !options?.allowSearchParam) return initialFilters
    // A URL with only pagination params (bookmark, reload) opens with the default filters
    if ([...searchParams.keys()].every((key) => PAGINATION_PARAMS.includes(key)))
      return initialFilters

    return transformUrlToFilter?.(searchParams)
  }

  const [filter, setFilter] = useState<TFilters | undefined>(transformFromUrl())
  const [selectedFilter, setSelectedFilter] = useState<TFilters | undefined>(transformFromUrl())

  const setFiltersToSearchParams = (filters: TFilters) => {
    const transformedFilters = transformFilterToUrl?.(filters)
    const { limit, sortBy, q } = readPagination()
    const searchParams = new URLSearchParams()
    searchParams.set('page', (INITIAL_PAGE + 1).toString())
    searchParams.set('limit', limit?.toString() || PAGINATION_LIMIT.toString())
    if (sortBy) searchParams.set('sortBy', `${sortBy.field}:${sortBy.order}`)
    if (q) searchParams.set('q', q)

    const urlFilters = transformedFilters === '' ? NO_FILTERS_PARAM : transformedFilters

    setSearchParams(`${searchParams.toString()}&${urlFilters}`, { replace: true })

    return searchParams
  }

  const onSelectedFilterChange = (newFilter?: TFilters) => {
    setSelectedFilter(newFilter)
    setFilter(newFilter)
    if (options?.allowSearchParam && newFilter) setFiltersToSearchParams(newFilter)

    console.log('Selected Filter Changed:', newFilter)
  }

  const resetFilters = () => {
    setSelectedFilter({ ...initialFilters })
    setFilter({ ...initialFilters })
    if (options?.allowSearchParam && transformFilterToUrl)
      setSearchParams(transformFilterToUrl({ ...initialFilters }))
  }

  const onFilterChange = (newFilter?: TFilters) => {
    setFilter(newFilter)
  }

  useEffect(() => {
    if (options?.allowSearchParam && page === null)
      navigate(
        `?page=${INITIAL_PAGE + 1}&limit=${PAGINATION_LIMIT}&${transformFilterToUrl?.(initialFilters) ?? ''}`,
        { replace: true }
      )
  }, [searchParams, navigate])

  return {
    filter,
    selectedFilter,
    onFilterChange,
    onSelectedFilterChange,
    resetFilters
  }
}
